import axios from 'axios'
import _ from 'underscore'
import store from '../state/store'

// All `tasks_overview` state is kept on the store so it survives navigating
// into an enquiry/lead and back to the Tasks rail entry — same pattern as the
// pipeline slice.

const slice = () => store.get().tasks_overview

const requestParams = (s, overrides = {}) => ({
  user_filter: s.user_filter,
  filter: s.filter,
  search: s.search,
  page: s.page,
  ...overrides
})

store.on('tasks_overview:index', function(overrides = {}) {
  const s = slice()
  const params = requestParams(s, overrides)
  const replacing = (params.page || 1) === 1

  slice().set(replacing
    ? { loading: true, tasks: [], more_results: false, page: 1 }
    : { loading: true })

  axios.get('/api/sales/tasks/overview', { params })
    .then((response) => {
      const incoming = response.data.tasks || []
      const existing = replacing ? [] : slice().tasks
      const merged = replacing
        ? incoming
        : existing.concat(_.reject(incoming, (t) => _.findWhere(existing, { id: t.id })))

      slice().set({
        loading: false,
        loaded: true,
        tasks: merged,
        more_results: response.data.more_results === true,
        filter_options: response.data.filter_options || [],
        user_filter_options: response.data.user_filter_options || [],
        assignees: response.data.assignees || [],
        page: params.page
      })
    })
    .catch((error) => {
      slice().set({ loading: false })
      window.handleError(error)
    })
})

store.on('tasks_overview:set_user_filter', function(value) {
  slice().set({ user_filter: value })
  store.emit('tasks_overview:index', { user_filter: value, page: 1 })
})

store.on('tasks_overview:set_filter', function(value) {
  slice().set({ filter: value })
  store.emit('tasks_overview:index', { filter: value, page: 1 })
})

store.on('tasks_overview:set_search', function(value) {
  slice().set({ search: value })
  store.emit('tasks_overview:index', { search: value, page: 1 })
})

store.on('tasks_overview:load_more', function() {
  const s = slice()
  if (s.loading || !s.more_results) return
  store.emit('tasks_overview:index', { page: (s.page || 1) + 1 })
})

// Inline edits (assignee / due_at) update the matching row in place rather
// than refetching, so the rep doesn't lose their scroll position or any
// already-loaded subsequent pages.
const replaceTaskLocally = (id, partial) => {
  const s = slice()
  const idx = _.findIndex(s.tasks, (t) => t.id === id)
  if (idx === -1) return
  s.tasks[idx].set(partial)
}

const updateTaskFields = (task, partial) => {
  if (!task.actions || !task.actions.can_edit) return

  const payload = _.extend({
    name: task.name,
    due_at: task.due_at,
    reminder: task.reminder,
    priority: task.priority,
    owner_id: task.owner_id,
    team: task.team,
    workflow_state: task.workflow_state
  }, partial)

  // Optimistic local update; the API response is authoritative below.
  replaceTaskLocally(task.id, partial)

  axios.put(`/api/sales/tasks/${task.id}`, { task: payload })
    .then((response) => {
      const updated = response.data || {}
      replaceTaskLocally(task.id, {
        due_at: updated.due_at,
        owner_id: updated.owner_id,
        owner: updated.owner,
        team: updated.team,
        priority: updated.priority,
        reminder: updated.reminder,
        workflow_state: updated.workflow_state,
        completed_at: updated.completed_at
      })
    })
    .catch(window.handleError)
}

store.on('tasks_overview:set_task_owner', function(task, assignee) {
  if (assignee.type === 'user') {
    updateTaskFields(task, { owner_id: assignee.id, owner: assignee })
  }
  else if (assignee.type === 'team') {
    updateTaskFields(task, { owner_id: null, owner: null, team: assignee.id })
  }
})

store.on('tasks_overview:set_task_due', function(task, due_at) {
  updateTaskFields(task, { due_at: due_at })
})

store.on('tasks_overview:toggle_task', function(task) {
  if (!task.actions || !task.actions.can_edit) return

  const next_state = task.workflow_state === 'complete' ? 'incomplete' : 'complete'

  axios.put(`/api/sales/tasks/${task.id}`, {
    task: {
      name: task.name,
      due_at: task.due_at,
      reminder: task.reminder,
      priority: task.priority,
      owner_id: task.owner_id,
      team: task.team,
      workflow_state: next_state
    }
  })
    .then(() => {
      // Refetch from page 1 — toggling complete moves the task between buckets,
      // so we want the API to decide what stays in view.
      store.emit('tasks_overview:index', { page: 1 })
    })
    .catch(window.handleError)
})
